import * as error from "../../lib/api/error";
import * as routeHelpers from "../../lib/routeHelpers";
import * as routes from "../../constants/routes";
import ApprovedPage from "../ApprovedPage";
import CongratulationsPage from "../CongratulationsPage";
import Demo from "../Demo";
import DuplicatePage from "../DuplicatePage";
import ECommPage from "../ECommPage";
import ErrorPage from "../ErrorPage";
import ForgotInvite from "../ForgotInvite";
import InterestDisclosurePage from "../InterestDisclosurePage";
import InviteApplicationPage from "../InviteApplicationPage";
import InviteLandingPage from "../InviteLandingPage";
import ModalContext, {ModalContextType} from "../Modal/ModalContext";
import NotFoundPage from "../NotFoundPage";
import OrganicApplicationPage from "../OrganicApplicationPage";
import OrganicLandingPage from "../OrganicLandingPage";
import ProcessingPage from "../ProcessingPage";
import ReaccessReferralSharePage from "../ReaccessReferralSharePage";
import React, {useState, useEffect} from "react";
import ReferralPage from "../ReferralPage";
import ReferralErrorPage from "../ReferralErrorPage";
import ReferralSharePage from "../ReferralSharePage";
import ReferralClaimPage from "../ReferralClaimPage";
import ReviewComplete from "../ReviewComplete";
import ReviewScreen from "../ReviewScreen";
import ScrollToTop from "../ScrollToTop";
import SecuredCardDepositPage from "../SecuredCardDepositPage";
import SecuredCardForgotPage from "../SecuredCardForgotPage";
import SecuredCardLandingPage from "../SecuredCardLandingPage";
import SecuredCardWelcomeBack from "../SecuredCardWelcomeBack";
import styles from "./App.module.css";
import SubmittedFundLaterPage from "../SubmittedFundLaterPage";
import ThankyouPage from "../ThankyouPage";
import TimeoutPage from "../TimeoutPage";
import {Switch, Route} from "react-router-dom";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import DebugGrid from "../DebugGrid";
import DuplicateDsnPage from "../DuplicateDsnPage";

const COMPONENT_NAME = "App";

export default function App() {
    const [modalContext, setModalContext] = useState<ModalContextType>({
        openModalIds: [],
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        openModal: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        closeModal: () => {},
    });

    function openModal(id: string) {
        setModalContext((prev: ModalContextType) => {
            return {
                ...prev,
                openModalIds: [...prev.openModalIds, id],
            };
        });
    }

    function closeModal(modalId: string) {
        setModalContext((prev) => {
            return {
                ...(prev as ModalContextType),
                openModalIds: prev.openModalIds.filter((id) => id !== modalId),
            };
        });
    }

    useEffect(() => {
        setModalContext({
            openModalIds: [],
            openModal,
            closeModal,
        });
    }, []);

    useEffect(() => {
        function handleError(e: ErrorEvent) {
            error.reportEvent(e);
        }

        function handleRejection(e: PromiseRejectionEvent) {
            e.preventDefault();
            error.reportRejection(e, COMPONENT_NAME);
        }

        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handleRejection);
        return () => {
            window.removeEventListener("error", handleError);
            window.removeEventListener("unhandledrejection", handleRejection);
        };
    }, []);

    const hasOpenModal = modalContext.openModalIds.length >= 1;

    return (
        <ModalContext.Provider value={modalContext}>
            <div
                aria-hidden={hasOpenModal ? "true" : undefined}
                className={hasOpenModal ? styles.noScrolling : undefined}
            >
                <ScrollToTop />
                <Switch>
                    <Route
                        exact={true}
                        path={routes.CONGRATULATIONS}
                        component={CongratulationsPage}
                    />

                    <Route
                        exact={true}
                        path={routes.APPROVED_SECURED}
                        render={(props) => <ApprovedPage {...props} isSecuredCard={true} />}
                    />

                    <Route exact={true} path={routes.APPROVED_DEFAULT}>
                        <ApprovedPage isSecuredCard={false} />
                    </Route>

                    <Route exact={true} path={routes.PROCESSING}>
                        <ProcessingPage />
                    </Route>

                    <Route exact={true} path={routes.THANK_YOU}>
                        <ThankyouPage />
                    </Route>

                    <Route exact={true} path={routes.DUPLICATE}>
                        <DuplicatePage />
                    </Route>

                    <Route exact={true} path={routes.DUPLICATE_DSN}>
                        <DuplicateDsnPage />
                    </Route>

                    <Route exact={true} path={routes.ELECTRONIC_AGREEMENT_FULL}>
                        <ECommPage />
                    </Route>

                    <Route exact={true} path={routes.INTEREST_DISCLOSURE}>
                        <InterestDisclosurePage showClose={true} />
                    </Route>

                    <Route exact={true} path={routes.TIMEOUT} component={TimeoutPage} />

                    <Route
                        exact={true}
                        path={routes.ORGANIC_LANDING}
                        component={OrganicLandingPage}
                    />

                    <Route
                        exact={true}
                        path={routes.ORGANIC_APP}
                        component={OrganicApplicationPage}
                    />

                    <Route
                        exact={true}
                        path={routes.INVITE_LANDING}
                        component={InviteLandingPage}
                    />

                    <Route
                        exact={true}
                        path={routes.INVITE_APP}
                        component={InviteApplicationPage}
                    />

                    <Route exact={true} path={routes.FORGOT_INVITE} component={ForgotInvite} />

                    <Route exact={true} path={routeHelpers.REVIEW_PATH} component={ReviewScreen} />

                    <Route exact={true} path={routes.ERROR}>
                        <ErrorPage />
                    </Route>

                    <Route exact={true} path={routes.REVIEW_COMPLETE} component={ReviewComplete} />

                    <Route
                        exact={true}
                        path={routes.SECURED_DEPOSIT}
                        component={SecuredCardDepositPage}
                    />

                    <Route
                        exact={true}
                        path={routes.SECURED_FUND_LATER_SUBMITTED}
                        component={SubmittedFundLaterPage}
                    />

                    <Route exact={true} path={routes.SECURED_CARD}>
                        <SecuredCardLandingPage />
                    </Route>

                    <Route
                        exact={true}
                        path={routes.WELCOME_BACK}
                        component={SecuredCardWelcomeBack}
                    />

                    <Route
                        exact={true}
                        path={routes.SECURED_FORGOT}
                        component={SecuredCardForgotPage}
                    />

                    <Route exact={true} path={routes.REFERRAL_PAGE} component={ReferralPage} />

                    <Route
                        exact={true}
                        path={routes.REFERRAL_SHARE}
                        component={ReferralSharePage}
                    />

                    <Route
                        exact={true}
                        path={routes.REFERRAL_CLAIM}
                        component={ReferralClaimPage}
                    />

                    <Route
                        exact={true}
                        path={routes.REACCESS_REFERRAL_SHARE}
                        component={ReaccessReferralSharePage}
                    />

                    <Route exact={true} path={routes.REFERRAL_ERROR}>
                        <ReferralErrorPage />
                    </Route>

                    <Route exact={true} path="/demo">
                        <Demo />
                    </Route>

                    <Route>
                        <NotFoundPage />
                    </Route>
                </Switch>
                {/* <DebugGrid /> */}
            </div>
        </ModalContext.Provider>
    );
}
